import { useState, useMemo } from "react";
import { Icon } from "@/lib/icons";
import { useAuth } from "@/context/AuthContext";
import InstallAppButton from "@/components/InstallAppButton";
import { ReportIssue } from "@/components/ReportIssue";
import { LogOut, X, Search, ChevronLeft } from "lucide-react";
import { useRail, Badge, isActiveItem } from "@/components/Sidebar";

const HOME_PAGES = ["dashboard", "my_tasks"];
const SECTION_ORDER = ["home", "my_membership", "membership", "gardens", "events", "fundraising", "communications", "governance", "settings"];
const BAR_ORDER = ["home", "membership", "events", "governance", "gardens", "fundraising", "communications", "my_membership", "settings"];
const total = (s) => s.items.reduce((n, i) => n + (i.badge || 0), 0);

// Phone sections: Home keeps Dashboard + My Tasks; the person's own pages get My Membership.
function usePhoneSections(rail) {
  return useMemo(() => {
    const all = rail.flatMap((g) => g.items);
    const sec = {
      home: { id: "home", label: "Home", icon: "LayoutDashboard", items: all.filter((i) => HOME_PAGES.includes(i.id)) },
      my_membership: { id: "my_membership", label: "My Membership", icon: "User", items: all.filter((i) => i.id.startsWith("mm_")) },
    };
    for (const g of rail) {
      if (g.id !== "home") sec[g.id] = { ...g, items: g.items.filter((i) => !HOME_PAGES.includes(i.id)) };
    }
    return SECTION_ORDER.map((id) => sec[id]).filter((s) => s && s.items.length);
  }, [rail]);
}

function Chip({ item, active, onPick }) {
  const on = isActiveItem(item, active);
  return (
    <button onClick={() => onPick(item)} data-testid={`m-chip-${item.id}`}
      className={`relative inline-flex items-center gap-2 px-3.5 min-h-[42px] rounded-xl border text-[14px] font-semibold transition-colors ${on ? "bg-olive border-olive text-ink" : "bg-white border-ink/15 text-ink active:bg-ink/5"}`}>
      {item.label}
      <Badge n={item.badge} />
    </button>
  );
}

const Chips = ({ items, active, onPick }) => <div className="flex flex-wrap gap-2">{items.map((i) => <Chip key={i.id} item={i} active={active} onPick={onPick} />)}</div>;

function AreaButton({ s, onClick }) {
  return (
    <button onClick={onClick} data-testid={`m-area-${s.id}`}
      className="relative flex flex-col items-center justify-center gap-2 min-h-[92px] rounded-xl border border-ink/15 bg-white px-2 text-center active:bg-ink/5">
      <Icon name={s.icon} className="w-6 h-6 text-dark-olive" />
      <span className="text-[13px] font-semibold leading-tight text-ink">{s.label}</span>
      <Badge n={total(s)} className="absolute top-2 right-2" />
    </button>
  );
}

function Account({ me, active }) {
  const { logout } = useAuth();
  return (
    <div className="border-t border-ink/10 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] space-y-3" data-testid="m-account">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{me.user.name}</div>
          <div className="text-[13px] text-ink/60 truncate">{me.user.seat ? `${me.user.seat} · ` : ""}{me.role.name}</div>
        </div>
        <button onClick={logout} data-testid="m-logout" className="inline-flex items-center gap-1.5 px-3 min-h-[40px] rounded-xl border border-ink/15 text-[14px] font-semibold text-ink/80"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <InstallAppButton variant="light" />
        <ReportIssue screen={active || "App"} variant="light" />
      </div>
    </div>
  );
}

// One sheet for both an area (its pages) and All (area buttons, drill in with Back).
function Sheet({ me, sheet, sections, extras, active, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [area, setArea] = useState(null);
  const query = q.trim().toLowerCase();
  const results = query ? sections.flatMap((s) => s.items).filter((i) => i.label.toLowerCase().includes(query)) : null;
  const isAll = sheet === "all";
  const shown = isAll ? sections.find((s) => s.id === area) : sections.find((s) => s.id === sheet);
  const title = results ? "Results" : shown ? shown.label : "All";
  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end min-[900px]:hidden" data-testid={isAll ? "m-all-sheet" : `m-area-sheet-${sheet}`}>
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-cream rounded-t-2xl max-h-[88vh] flex flex-col animate-rise shadow-2xl">
        <div className="px-4 pt-4 pb-3 border-b border-ink/10 space-y-3">
          <div className="flex items-center gap-2">
            {isAll && area && !results && (
              <button onClick={() => setArea(null)} data-testid="m-sheet-back" className="inline-flex items-center gap-1 px-2.5 min-h-[40px] rounded-xl border border-ink/15 text-[14px] font-semibold text-ink/80"><ChevronLeft className="w-4 h-4" /> Back</button>
            )}
            <h2 className="flex-1 font-zilla text-xl font-semibold text-ink truncate" data-testid="m-sheet-title">{title}</h2>
            <button onClick={onClose} aria-label="Close" data-testid="m-sheet-close" className="w-11 h-11 rounded-xl border border-ink/15 flex items-center justify-center text-ink/70"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-2 bg-white border border-ink/15 rounded-xl px-3 min-h-[44px] focus-within:border-maroon/50">
            <Search className="w-4 h-4 text-ink/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find any screen" data-testid="m-find-screen" className="bg-transparent flex-1 text-[15px] py-2 outline-none" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="m-sheet-body">
          {results ? (results.length ? <Chips items={results} active={active} onPick={onPick} />
            : <div className="text-[14px] text-ink/50" data-testid="m-search-empty">No screens match "{q}".</div>)
            : shown ? <Chips items={shown.items} active={active} onPick={onPick} />
            : <div className="grid grid-cols-3 gap-2">{extras.map((s) => <AreaButton key={s.id} s={s} onClick={() => setArea(s.id)} />)}</div>}
        </div>
        {isAll && <Account me={me} active={active} />}
      </div>
    </div>
  );
}

function BarTab({ id, label, icon, badge, on, onClick }) {
  return (
    <button onClick={onClick} data-testid={`m-tab-${id}`} className="flex-1 flex flex-col items-center gap-1 py-1.5 min-w-0">
      <span className={`relative w-11 h-11 rounded-xl border flex items-center justify-center transition-colors ${on ? "bg-olive border-olive text-ink" : "bg-cream/5 border-cream/15 text-cream/85"}`}>
        <Icon name={icon} className="w-5 h-5" />
        <Badge n={badge} className="absolute -top-1.5 -right-1.5" />
      </span>
      <span className={`text-[11px] leading-tight truncate max-w-full ${on ? "text-cream font-semibold" : "text-cream/70"}`}>{label}</span>
    </button>
  );
}

export default function MobileNav({ me, active, onSelect }) {
  const { setPersona } = useAuth();
  const rail = useRail(me);
  const sections = usePhoneSections(rail);
  const [sheet, setSheet] = useState(null);
  // Four areas; if a role can't see one, the next visible area takes that slot.
  const visible = BAR_ORDER.map((id) => sections.find((s) => s.id === id)).filter(Boolean);
  const spare = visible.filter((s) => !BAR_ORDER.slice(0, 4).includes(s.id));
  const tabs = BAR_ORDER.slice(0, 4).map((id) => visible.find((s) => s.id === id) || spare.shift()).filter(Boolean);
  const extras = sections.filter((s) => !tabs.includes(s));
  const currentSec = sections.find((s) => s.items.some((i) => isActiveItem(i, active)));
  const pick = (i) => { setSheet(null); if (i.persona) setPersona(i.persona); else onSelect(i.target); };
  const tabOn = (id) => (sheet ? sheet === id : currentSec?.id === id);
  return (
    <>
      <nav className="min-[900px]:hidden fixed bottom-0 inset-x-0 z-40 bg-ink px-2 pt-1.5 pb-[calc(6px+env(safe-area-inset-bottom))] flex" data-testid="m-bottom-bar">
        {tabs.map((s) => (
          <BarTab key={s.id} id={s.id} label={s.short || s.label} icon={s.icon} badge={total(s)} on={tabOn(s.id)} onClick={() => setSheet(s.id)} />
        ))}
        <BarTab id="all" label="All" icon="MoreHorizontal" badge={extras.reduce((n, s) => n + total(s), 0)}
          on={sheet ? sheet === "all" : !!currentSec && !tabs.includes(currentSec)} onClick={() => setSheet("all")} />
      </nav>
      {sheet && <Sheet key={sheet} me={me} sheet={sheet} sections={sections} extras={extras} active={active} onPick={pick} onClose={() => setSheet(null)} />}
    </>
  );
}
